import { FC, ReactNode } from 'react';
import styled, { keyframes, css } from 'styled-components';

const wave = keyframes`
	0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const pulse = keyframes`
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.4;
	}
	100% {
		opacity: 1;
	}
`;

interface StyledSkeletonProps {
	animation?: string;
	borderRadius?: string;
	hasChildren: boolean;
	children?: ReactNode | ReactNode[];
}

const StyledSkeleton = styled.span<StyledSkeletonProps>`
	background-color: var(--skeleton-placeholder-bg, --base-10);

	display: block;
	line-height: 1;
	${({ animation }) =>
		animation &&
		css`
			animation: ${animation === 'pulse' && pulse} 1.5s ease-in-out infinite;
		`}
	${props =>
		props.hasChildren &&
		css`
			& > * {
				visibility: hidden;
			}
		`};

	${({ animation }) =>
		animation !== 'pulse' &&
		css`
			position: relative;
			overflow: hidden;

			&::after {
				background-image: var(--skeleton-placeholder-bg-gradient, --base-10);
				animation: ${wave} 1s linear infinite;
				content: '';
				position: absolute;
				transform: translateX(-100%);
				inset: 0;
			}
		`}
`;

interface SkeletonProps {
	className?: string;
	width?: string | number;
	height?: string | number;
	margin?: string | number;
	borderRadius?: string;
	animation?: string;
	children?: ReactNode | ReactNode[];
}

const Skeleton: FC<SkeletonProps> = ({ className = '', width, height, children, borderRadius, animation, margin }) => {
	const hasChildren = Boolean(children);
	if (hasChildren) {
		return (
			<StyledSkeleton
				className={className}
				style={{ width, height, borderRadius, margin }}
				hasChildren={hasChildren}
				animation={animation}
			>
				{children}
			</StyledSkeleton>
		);
	}
	return (
		<StyledSkeleton
			className={className}
			style={{ width, height, borderRadius, margin }}
			hasChildren={hasChildren}
			animation={animation}
		/>
	);
};

Skeleton.defaultProps = {
	height: '100%',
	width: '100%',
	margin: undefined,
	borderRadius: '',
	className: '',
	animation: 'wave'
};

export default Skeleton;
